import { ButtonIcon, ButtonProps } from "@insite/mobius/Button/Button";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { IconProps } from "@insite/mobius/Icon";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import TrashAlt from "@insite/mobius/Icons/Trash2";
import { RadioGroupProps } from "@insite/mobius/RadioGroup";
import { TextFieldPresentationProps } from "@insite/mobius/TextField/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockVariant, BlockVariantSide } from "@store/Components/Common/Block";
import { BlockProps } from "@store/Components/Common/Block/Block";
import { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { gridGapStyles } from "@store/theme/common/mixins";
import { inputLabelStyles } from "@store/theme/common/mixins/form";
import { formFieldSpacingStyles } from "@store/theme/common/props/form";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import { css } from "styled-components";

export interface StoreRetailModeViewStyles {
    wrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    activateRetailPriceInputWrapper?: InjectableCss;
    addRowWrapper?: InjectableCss;
    addRowButton?: ButtonProps;
    saveButton?: ButtonProps;
    radioGroup?: RadioGroupProps;
    removeButton?: ButtonProps;
    removeIcon?: IconProps;
    tierPricingTextField?: TextFieldPresentationProps;
    increasePercentInputWrapper?: TextFieldPresentationProps;
    retailModeSettingsBlock?: BlockProps;
    retailModeSettingsSavingButtonBlock?: BlockProps;
    inputLabel?: TypographyPresentationProps;
    signTypography?: TypographyPresentationProps;
    firstSignTypography?: TypographyPresentationProps;
    container?: InjectableCss;
    tableGridHead?: GridContainerProps;
    tableGridBody?: GridContainerProps;
    tableGridRowContainer?: GridContainerProps;
    tableGridRow?: GridItemProps;
    headerText?: TypographyProps;
    marginHeaderGridItem?: GridItemProps;
    toHeaderGridItem?: GridItemProps;
    fromHeaderGridItem?: GridItemProps;
    fromInputGridItem?: GridItemProps;
    toInputGridItem?: GridItemProps;
    marginInputGridItem?: GridItemProps;
    removeButtonGridCell?: GridItemProps;
    mobileFromLabelGridItem?: GridItemProps;
    mobileMarginLabelGridItem?: GridItemProps;
    mobileRemoveButtonGridCell?: GridItemProps;
    mobileRemoveButton?: ButtonProps;
    mobileMarginTierPricingTextField?: TextFieldPresentationProps;
    mobileRemoveIcon?: IconProps;
}

export default {

    // Grid Item Props
    container: {
        ...tableGridProps.container,
        css: css`
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    tableGridHead: {
        ...tableGridProps.tableGridHead,
        css: css`
            ${tableGridProps.tableGridHead?.css}
            background-color: ${getColor("secondary")};
        `,
    },
    tableGridBody: tableGridProps.tableGridBody,
    tableGridRowContainer: {
        ...tableGridProps.tableGridRowContainer,
        gap: 0,
        css: css`
            ${gridGapStyles(
                ["0px"],
                ["20px"],
            )};
            ${GridOffset} {
                margin: 0;
            },
        `,
    },
    tableGridRow: tableGridProps.tableGridRow,
    fromHeaderGridItem: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 3, 3, 3]
    },
    toHeaderGridItem: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 3, 3, 3]
    },
    marginHeaderGridItem: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 3, 3, 3]
    },
    fromInputGridItem: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 3, 3, 3],
        css: css`
            align-items: center;
        `,
    },
    toInputGridItem: {
        ...tableGridProps.tableGridCell,
        width: [6, 9, 3, 3, 3],
        css: css`
            align-items: center;
        `,
    },
    marginInputGridItem: {
        ...tableGridProps.tableGridCell,
        width: [9, 6, 3, 3, 3],
        css: css`
            align-items: center;
        `,
    },
    removeButtonGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 2, 3, 3],
        css: css`
            justify-content: end;
        `,
    },
    headerText: tableGridProps.textHeader,
    mobileMarginLabelGridItem: {
        width: [5, 3, 0, 0, 0],
        css: css`
            align-self: center;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.bold};        
        `,
    },
    mobileFromLabelGridItem: {
        width: [3, 3, 0, 0, 0],
        css: css`
            align-self: center;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    mobileRemoveButtonGridCell: {
        width: [3, 3, 0, 0, 0],
        css: css`
            display: flex;
            justify-content: end;
            align-self: center;
        `,
    },
    mobileRemoveButton: {
        hoverMode: "lighten",
        css: css`
                color: ${getColor("danger")};
                background-color: ${getColor("primary.contrast")};                
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.base};
                border: none;
                min-width: 0;
                padding: 2px;
        `,
    },
    mobileRemoveIcon: {
        src: TrashAlt,
        height: 15,
        css: css`
            margin-top: 3px;
            margin-left: -2px;
        `,
    },
    wrapper: {
        css: css``,
    },
    addRowWrapper: {
        css: css`
            margin-top: 1em;
            justify-content: flex-end;
        `,
    },
    addRowButton: {
        variant: "primary",
        css: css<any>`
            ${({ theme }) => theme?.button?.primary?.css}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["100%"], properties: ["width"] }],
                    mediaOptions.max,
                )}

            ${ButtonIcon} {
                top: 0;
                color: inherit;
            }
        `,
        icon: {
            src: PlusCircle,
            position: "right",
        },
    },
    saveButton: {
        variant: "primary",
        css: css`
            ${({ theme }) => theme?.button?.primary?.css}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["100%"], properties: ["width"] }],
                    mediaOptions.max,
                )}
        `,
    },
    inputWrapper: {
        css: css`
            ${formFieldSpacingStyles}
            max-width: 200px;
        `,
    },
    activateRetailPriceInputWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${formFieldSpacingStyles}
        `,
    },
    inputLabel: {
        css: css`
            ${inputLabelStyles()}
        `,
    },
    signTypography: {
        css: css`
            padding: 0px 8px 0px 4px;
            display: flex;
            align-items: center;
        `,
    },
    firstSignTypography: {
        css: css`
            padding: 0px 8px 0px 5px;
            display: flex;
            align-items: center;
        `,
    },
    radioGroup: {
        css: css`
            margin-top: 5px;
        `,
    },
    removeButton: {
        color: "danger",
        hoverMode: "lighten",
        css: css`
            ${({ theme }) => theme?.button?.defaultProps?.css}
            min-width: 0;
            padding: 10px;
            margin: 0 0 0 5px;
        `,
    },
    removeIcon: {
        src: TrashAlt,
    },
    retailModeSettingsBlock: {
        preserveSpacing: true,
    },
    retailModeSettingsSavingButtonBlock: {
        preserveSpacing: true,
        variant: BlockVariant.Separated,
        variantSide: BlockVariantSide.Top,
    },
    tierPricingTextField: {
        cssOverrides: {
            formField: css`
                width: auto;
                max-width: 150px;
                min-width: 115px;
            `,
        },
    },
    increasePercentInputWrapper: {},
} as StoreRetailModeViewStyles;
